import { useI18n } from '@superbet-group/web.lib.vue-utilities';
import { computed } from '@vue/composition-api';
import { sortBy as _sortBy } from 'lodash';
import { BonusAwardedParams } from '@src/app/promotionHub/bonuses/utils/types';
import { Condition } from '@app/promotionHub/shared/types/Condition';
import {
    BonusCustomContentConditions,
    CustomContentConditions,
    SharedCustomContentConditions,
} from '@app/promotionHub/shared/types/CustomContent';
import {
    isBingoBonus,
    isFreeRewardItemBonus,
    isFreeSpinBonus,
    isWageringBonus,
} from '@app/promotionHub/bonuses/utils/bonusTypeHelpers';
import { BonusKind } from '@core/models/bonus/Bonus';

export function useCustomConditionsPreview(customConditions: Condition[], kind: BonusKind): Condition[] {
    const excludedCustomConditions = new Set<CustomContentConditions>(
        [
            BonusCustomContentConditions.bonusBetEligibility,
            SharedCustomContentConditions.sharedExpirationDate,
            SharedCustomContentConditions.sharedTermsAndConditions,
        ],
    );

    const sortedConditions = kind === BonusKind.Comtrade
        ? _sortBy(customConditions, ({ type }) => type === SharedCustomContentConditions.sharedBetsMustSettle ? 0 : 1)
        : customConditions;

    return sortedConditions.filter((item) => !excludedCustomConditions.has(item.type));
}

export function useBonusAwarded({
    bonusItemsAwarded,
    bonusAwarded,
    bonusPotentialWin,
    type,
    bonusSubtype,
    isBonusOnHold,
}: BonusAwardedParams) {
    const { translate, translatePluralized, formatCurrency } = useI18n();

    const bonusAwardedAmount = computed(() => {
        if (isFreeRewardItemBonus(type.value)) {
            return bonusItemsAwarded.value;
        } else if (isWageringBonus(bonusSubtype.value)) {
            return formatCurrency(isBonusOnHold.value ? bonusAwarded.value : bonusPotentialWin.value);
        } else {
            return formatCurrency(bonusAwarded.value);
        }
    });
    const bonusAwardedText = computed(() => {
        if (isFreeSpinBonus(type.value)) {
            return translatePluralized('bonus.spinsPluralized', bonusItemsAwarded.value).toUpperCase();
        } else if (isBingoBonus(type.value)) {
            return translatePluralized('bonus.ticketsPluralized', bonusItemsAwarded.value).toUpperCase();
        } else {
            return translate('currencyAbbreviation');
        }
    });

    return {
        bonusAwardedAmount,
        bonusAwardedText,
    };
}
